import { portalLocales, type PortalLocale } from '@worldcoo/monolith/portals';
import { useRouteContext, useRouter } from '@tanstack/react-router';
import { i18n } from '@lingui/core';
import { getRequestContext } from './data';
import { updateRouterLocale } from '@/router';
import { translateSlug } from './slug-rewrites';

export function useTranslation() {
	const { portalId, locale } = useRouteContext({ from: '__root__' });
	const router = useRouter();

	const changeLanguage = (lang: PortalLocale, currentCampaignId?: string) => {
		loadLandingTranslation(lang);
		if (typeof document !== 'undefined') {
			document.cookie = `language=${lang}; path=/; max-age=31536000`;
		}
		updateRouterLocale(lang);
		if (router.options.context) {
			(router.options.context as { locale: PortalLocale }).locale = lang;
		}

		if (typeof window !== 'undefined') {
			const segments = window.location.pathname.split('/').filter(Boolean);
			const isLocaleFirst = (portalLocales as readonly string[]).includes(segments[0] ?? '');
			const restSegments = isLocaleFirst ? segments.slice(1) : segments;

			let newCampaignSlug: string | undefined;
			if (currentCampaignId) {
				const qc = (router.options.context as { queryClient?: { getQueryData(key: unknown[]): unknown } } | undefined)
					?.queryClient;
				if (qc) {
					const cached = qc.getQueryData([portalId, locale, 'campaigns', currentCampaignId]) as
						| { slugsByLocale?: Record<string, string>; id?: string }
						| undefined;
					newCampaignSlug = cached?.slugsByLocale?.[lang] ?? cached?.id ?? currentCampaignId;
				}
			}

			const newLegalSlug = !currentCampaignId && restSegments[0] ? translateSlug(restSegments[0], lang) : undefined;

			const newRestSegments =
				newCampaignSlug !== undefined
					? [newCampaignSlug, ...restSegments.slice(1)]
					: newLegalSlug !== undefined
						? [newLegalSlug, ...restSegments.slice(1)]
						: restSegments;

			const newPath = '/' + [lang, ...newRestSegments].join('/') + window.location.search;
			router.history.push(newPath);
		}

		getRequestContext({ data: lang }).then((ctx) => {
			if (router.options.context) {
				Object.assign(router.options.context, ctx);
			}
		});
	};

	return { language: locale, changeLanguage };
}

export const landingLocales = ['es', 'en', 'ca', 'it'] as const;
export type LandingLocale = (typeof landingLocales)[number];

export async function loadLandingTranslation(locale: PortalLocale) {
	const { messages } = await import(`../locales/${locale}.po`);
	i18n.loadAndActivate({ locale, messages });
	return i18n;
}
